import { MenuDTOTypes } from '@repo/experience-api-client';
import {
  BorderStyle,
  Box,
  Color,
  Display,
  DisplayPosition,
  Overflow,
  ResponsiveValue,
  ScreenReaderLiveRegion,
  SpaceScale,
  Visible,
  VisuallyHidden,
  withCitrusTheme,
} from '@gousto-internal/citrus-react';
import { KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import { centerCollectionAtIndex } from './utils';
import { MenuCollectionButton } from './MenuCollectionButton';
import { useMenuCollectionsScroll } from '../../hooks/useMenuCollectionsScroll';
import { MenuCollectionFilterScrollButton } from './ScrollButton';
import { queueTrackingEvent } from '../../event-tracking/queueTrackingEvent';
import { TRACKING_EVENT_NAMES } from '../../event-tracking/events';

export const MENU_TABPANEL_ID = 'menu-tabpanel';
export const MENU_COLLECTIONS_SCROLLABLE_CONTAINER_ID =
  'menu-collection-filters-scrollable-container';
const SCROLL_DISTANCE = 200;
export const MENU_COLLECTION_FILTER_PADDING_H: ResponsiveValue<SpaceScale> = 2;

type MenuCollectionFiltersProps = {
  collections: MenuDTOTypes.MenuCollection[];
  activeCollectionSlug: string;
  onCollectionClick: (slug: string) => void;
  recipeContainerId: string;
};

export function MenuCollectionFilters(props: MenuCollectionFiltersProps) {
  const { collections, activeCollectionSlug, onCollectionClick, recipeContainerId } = props;

  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const collectionRefs = useRef<HTMLButtonElement[]>([]);

  const { canScrollLeft, canScrollRight, handleScrollX } =
    useMenuCollectionsScroll(scrollableContainerRef);

  const isSelectedCollection = useCallback(
    (slug: string) => {
      return activeCollectionSlug === slug;
    },
    [activeCollectionSlug],
  );

  const handleCollectionClick = (slug: string, index: number, name: string) => {
    return function handleCollectionClickForSlug() {
      queueTrackingEvent(TRACKING_EVENT_NAMES.MENU_COLLECTION_FILTER_CLICK, {
        filter_type: 'collection',
        filter_slug: slug,
        filter_name: name,
      });
      centerCollectionAtIndex(index, scrollableContainerRef, collectionRefs);
      onCollectionClick(slug);
    };
  };

  const handleCollectionKeyUp = useCallback((collectionIdx: number) => {
    return (e: KeyboardEvent<HTMLButtonElement>) => {
      const nextTab = collectionRefs.current[collectionIdx + 1];
      const prevTab = collectionRefs.current[collectionIdx - 1];

      if (e.key === 'ArrowRight' && nextTab) {
        nextTab.focus();
      }

      if (e.key === 'ArrowLeft' && prevTab) {
        prevTab.focus();
      }
    };
  }, []);

  const addCollectionRef = useCallback(
    (collectionIdx: number) => (htmlButtonRef: HTMLButtonElement | null) => {
      if (htmlButtonRef) {
        collectionRefs.current[collectionIdx] = htmlButtonRef;
      }
    },
    [],
  );

  const activeCollectionName = useMemo(() => {
    const activeCollectionObj = collections.find(
      (collection) => collection.slug === activeCollectionSlug,
    );

    return activeCollectionObj ? activeCollectionObj.name : '';
  }, [collections, activeCollectionSlug]);

  return (
    <MenuCollectionContainer
      role="tablist"
      aria-label="Menu category filters"
      display={Display.Flex}
      paddingH={MENU_COLLECTION_FILTER_PADDING_H}
      bg={Color.White}
      borderBottomWidth={0.5}
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_50}
      position={DisplayPosition.Relative}
    >
      {canScrollLeft ? (
        <Visible display={[Display.None, Display.Block]}>
          <MenuCollectionFilterScrollButton
            onClick={() => handleScrollX({ direction: 'left', distance: SCROLL_DISTANCE })}
            aria-label="Scroll menu collections left"
            direction="left"
            aria-controls={MENU_COLLECTIONS_SCROLLABLE_CONTAINER_ID}
          />
        </Visible>
      ) : null}
      <Box
        id={'menu-collection-filters-scrollable-container'}
        overflowX={Overflow.Scroll}
        display={Display.Flex}
        width="100%"
        ref={scrollableContainerRef}
      >
        {collections.map((collection, index) => (
          <MenuCollectionButton
            key={collection.slug}
            onClick={handleCollectionClick(collection.slug, index, collection.name)}
            onKeyUp={handleCollectionKeyUp(index)}
            ref={addCollectionRef(index)}
            isSelected={isSelectedCollection(collection.slug)}
            aria-controls={recipeContainerId}
          >
            {collection.name}
          </MenuCollectionButton>
        ))}
      </Box>
      {canScrollRight ? (
        <Visible display={[Display.None, Display.Block]}>
          <MenuCollectionFilterScrollButton
            onClick={() => handleScrollX({ direction: 'right', distance: SCROLL_DISTANCE })}
            aria-label="Scroll menu collections right"
            direction="right"
            aria-controls={MENU_COLLECTIONS_SCROLLABLE_CONTAINER_ID}
          />
        </Visible>
      ) : null}
      <VisuallyHidden>
        <ScreenReaderLiveRegion
          urgency="polite"
          data-testid="recipe-filter-announcement"
          style={{ visibility: 'hidden' }}
        >
          Displaying recipes in collection {activeCollectionName}
        </ScreenReaderLiveRegion>
      </VisuallyHidden>
    </MenuCollectionContainer>
  );
}

const MenuCollectionContainer = withCitrusTheme(Box, (theme) => [
  {
    position: 'relative',
  },
  theme.responsiveSpacing([3, null, 0], ['paddingTop']),
]);
